import { z } from 'zod';
import { DomainError } from '../errors.js';
import { FUEL_TYPES } from '../voyage/lists.js';
import { REPORT_SECTIONS } from '../voyage/report-sections.js';
import type { ParseResult, SchemaIssue, VoyageReportKind } from '../voyage/schemas.js';
import {
  canonicalDec,
  decAdd,
  decCmp,
  decIsZero,
  decNeg,
  decRound,
  decSub,
  decSum,
  formatDec,
  isDecimalText,
} from './decimal.js';
import { optAmount, optEnum, optId, optNote, optText, resultOf, typed } from './fields.js';
import type { LedgerLine } from './ledger.js';

/**
 * Tüketimin tek kaynağı sefer raporudur (ADR 0117 §2; ortak belgesi §9). Rapor verisinin isteğe
 * bağlı `consumption` bölümü: dönem başlangıcı (bitiş raporun zamanı), operasyon ve ekipman–yakıt–
 * tank satırları; isteğe bağlı ölçülmüş dönem ayrımları (`splits`). Şema sürümü değişmez; eski
 * raporların verisi ve hash'i değişmez. Taslak eksik kaydedilir, zorunlular gönderimde
 * (`consumptionSubmitIssues`). Miktarlar ondalık metindir (§11). Rapor kesinleşince satırlardan
 * defter hareketi türer (`reportConsumptionPosting`) ve eski `rob[]` / Noon `fuel.fuels[]` alanları
 * defterden anlık görüntü olarak yazılır (`consumptionSnapshot`). Saf modül.
 */

export const CONSUMPTION_OPERATIONS = [
  'sea',
  'manoeuvring',
  'port',
  'anchor',
  'drift',
  'canal_strait',
  'waiting',
] as const;
export type ConsumptionOperation = (typeof CONSUMPTION_OPERATIONS)[number];

export const CONSUMPTION_METHODS = [
  'flowmeter',
  'tank_level',
  'bunker_delivery',
  'calculated',
  'estimated',
] as const;
export type ConsumptionMethod = (typeof CONSUMPTION_METHODS)[number];

export const CONSUMPTION_QUALITIES = ['measured', 'calculated', 'estimated'] as const;
export type ConsumptionQuality = (typeof CONSUMPTION_QUALITIES)[number];

/** Sayaç birimi; yalnız `mt` sayacının farkı doğrudan tüketimle karşılaştırılır. */
export const METER_UNITS = ['mt', 'm3', 'l', 'kg'] as const;
export type MeterUnit = (typeof METER_UNITS)[number];

export const SPLIT_REASONS = [
  'year',
  'port_arrival',
  'fuel_change',
  'regulatory_boundary',
  'company_change',
] as const;
export type SplitReason = (typeof SPLIT_REASONS)[number];

export const CONSUMPTION_LINES_MAX = 40;
export const CONSUMPTION_SPLITS_MAX = 10;
export const CONSUMPTION_KEY_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

const optDatetime = z.iso
  .datetime(typed('INVALID_DATETIME'))
  .nullish()
  .transform((value) => value ?? null);

/** İşaretli ondalık (sıcaklık). */
const optSigned = z
  .string(typed('INVALID_DECIMAL'))
  .refine(isDecimalText, { message: 'INVALID_DECIMAL', abort: true })
  .transform(canonicalDec)
  .nullish()
  .transform((value) => value ?? null);

const meterSchema = z
  .object({
    start: optAmount(),
    end: optAmount(),
    unit: optEnum(METER_UNITS),
    deviceId: optText(60),
    /** Sayaç sıfırlama ya da taşma: anı ve taşma değeri (T14). */
    reset: z
      .object({ at: optDatetime, rolloverAt: optAmount({ positive: true }) })
      .nullish()
      .transform((value) => value ?? null),
  })
  .nullish()
  .transform((value) => value ?? null);

const lineSchema = z.object({
  key: z.string(typed('INVALID_TEXT')).trim().regex(CONSUMPTION_KEY_PATTERN, 'INVALID_KEY'),
  equipmentId: optId,
  fuelTypeId: optId,
  tankId: optId,
  /** Parti: yakıt alımı (BDN); bilinmiyorsa boş. */
  batchId: optId,
  method: optEnum(CONSUMPTION_METHODS),
  quality: optEnum(CONSUMPTION_QUALITIES),
  massMt: optAmount({ max: '100000' }),
  meter: meterSchema,
  runningHours: optAmount({ max: '10000' }),
  density15: optAmount({ max: '2' }),
  tempC: optSigned,
  /** Hesap dayanağı ("Bitiş − Başlangıç", tahmin yöntemi …). */
  basis: optText(200),
  remarks: optNote(500),
});
export type ConsumptionLine = z.output<typeof lineSchema>;

const splitSchema = z.object({
  at: optDatetime,
  reason: optEnum(SPLIT_REASONS),
  basis: optText(200),
  /** Ayrım anından **önceki** kısmın yakıt bazında ölçülmüş tüketimi; kalanı sonrası. */
  lines: z
    .array(
      z.object({ fuelTypeId: optId, massMt: optAmount({ max: '100000' }) }),
      typed('INVALID_LIST'),
    )
    .max(20, 'TOO_MANY'),
});
export type ConsumptionSplit = z.output<typeof splitSchema>;

export const consumptionSectionSchema = z.object({
  /** Dönem başlangıcı (UTC); bitiş raporun zamanıdır. Aralık `[başlangıç, bitiş)`. */
  periodStart: optDatetime,
  operation: optEnum(CONSUMPTION_OPERATIONS),
  lines: z
    .array(lineSchema, typed('INVALID_LIST'))
    .max(CONSUMPTION_LINES_MAX, 'TOO_MANY')
    .nullish()
    .transform((value) => value ?? []),
  splits: z
    .array(splitSchema, typed('INVALID_LIST'))
    .max(CONSUMPTION_SPLITS_MAX, 'TOO_MANY')
    .nullish()
    .transform((value) => value ?? null),
});
export type ConsumptionSection = z.output<typeof consumptionSectionSchema>;

/** Sefer raporu şemasının bölümü (ADR 0057 §3 emsali): isteğe bağlı. */
export const consumptionSection = () => consumptionSectionSchema.nullish();

export function parseConsumption(input: unknown): ParseResult<ConsumptionSection> {
  return resultOf(consumptionSectionSchema.safeParse(input));
}

/** Tüketim bölümü olan rapor türleri: ROB tablosu olanlar ve Noon'un yakıt tablosu. */
export function reportHasConsumption(kind: VoyageReportKind): boolean {
  return kind === 'noon' || (REPORT_SECTIONS[kind]?.includes('rob') ?? false);
}

/** Rapor verisindeki bölüm; yoksa ya da biçim dışıysa `null`. */
export function consumptionOf(data: unknown): ConsumptionSection | null {
  const raw =
    data !== null && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>).consumption
      : undefined;
  if (raw === null || raw === undefined) return null;
  const parsed = parseConsumption(raw);
  return parsed.ok ? parsed.value : null;
}

// ---------- Süre, aralık, sayaç ----------

/** Dönem süresi (saat), UTC anlarından: gemi saati değişikliği süreyi etkilemez (ADR 0065). */
export function consumptionDurationHours(start: string, end: string): number {
  return (Date.parse(end) - Date.parse(start)) / 3_600_000;
}

export interface ConsumptionInterval {
  reportId: string;
  start: string;
  end: string;
}

/** `[a.start, a.end)` ile `[b.start, b.end)` kesişiyor mu. */
export function intervalsOverlap(
  a: Pick<ConsumptionInterval, 'start' | 'end'>,
  b: Pick<ConsumptionInterval, 'start' | 'end'>,
): boolean {
  return Date.parse(a.start) < Date.parse(b.end) && Date.parse(b.start) < Date.parse(a.end);
}

/**
 * Geminin kesinleşmiş aralıkları zamanda örtüşemez (§2.5; T16): bir raporun toplamı ile alt
 * dönemi aynı fiziksel tüketimi iki kez taşıyamaz. Aynı raporun eski revizyonu sayılmaz
 * (yeniden gönderimde ters kayıtla kapanır).
 */
export function assertNoOverlap(
  candidate: ConsumptionInterval,
  existing: readonly ConsumptionInterval[],
): void {
  const clash = existing.find(
    (other) => other.reportId !== candidate.reportId && intervalsOverlap(candidate, other),
  );
  if (clash) throw new DomainError('CONSUMPTION_OVERLAP', clash.reportId, clash.reportId);
}

/** Dönem başlangıcı önerisi: raporun zamanından önceki son raporun zamanı (Noon zinciri). */
export function suggestPeriodStart(
  reportedAt: string,
  reportTimes: readonly string[],
): string | null {
  const end = Date.parse(reportedAt);
  let best: number | null = null;
  for (const time of reportTimes) {
    const at = Date.parse(time);
    if (at < end && (best === null || at > best)) best = at;
  }
  return best === null ? null : new Date(best).toISOString();
}

/** Aralığın içindeki yıl sınırları (1 Ocak 00:00 UTC); bitiş hariç. */
export function yearBoundariesIn(start: string, end: string): string[] {
  const from = new Date(start);
  const to = Date.parse(end);
  const result: string[] = [];
  for (let year = from.getUTCFullYear() + 1; ; year += 1) {
    const at = Date.UTC(year, 0, 1);
    if (at >= to) break;
    if (at > Date.parse(start)) result.push(new Date(at).toISOString());
  }
  return result;
}

export type MeterDelta = { ok: true; delta: string } | { ok: false; code: 'METER_NEGATIVE' };

/**
 * Sayaç farkı (§2.7; T14): bitiş − başlangıç. Bitiş başlangıçtan küçükse yalnız sıfırlama/taşma
 * kaydıyla kabul edilir: (taşma − başlangıç) + bitiş; aksi `METER_NEGATIVE`.
 */
export function meterDelta(meter: {
  start: string | null;
  end: string | null;
  unit: string | null;
  deviceId: string | null;
  reset: { at: string | null; rolloverAt: string | null } | null;
}): MeterDelta | null {
  if (meter.start === null || meter.end === null) return null;
  if (decCmp(meter.end, meter.start) >= 0) {
    return { ok: true, delta: formatDec(decSub(meter.end, meter.start)) };
  }
  const rollover = meter.reset?.rolloverAt ?? null;
  if (rollover === null || decCmp(rollover, meter.start) < 0) {
    return { ok: false, code: 'METER_NEGATIVE' };
  }
  return { ok: true, delta: formatDec(decAdd(decSub(rollover, meter.start), meter.end)) };
}

// ---------- Gönderim kuralları ----------

const LINE_REQUIRED = [
  'equipmentId',
  'fuelTypeId',
  'tankId',
  'method',
  'quality',
  'massMt',
] as const satisfies readonly (keyof ConsumptionLine)[];

/** Yakıt türü bazında satır toplamları (§2.6): `fuelTypeId` → MT. */
export function fuelTotals(lines: readonly ConsumptionLine[]): Record<string, string> {
  const totals: Record<string, string> = {};
  for (const line of lines) {
    if (!line.fuelTypeId || line.massMt === null) continue;
    totals[line.fuelTypeId] = formatDec(decAdd(totals[line.fuelTypeId] ?? '0', line.massMt));
  }
  return totals;
}

/** Bölümün toplam tüketimi (MT). */
export function consumptionTotal(lines: readonly ConsumptionLine[]): string {
  return formatDec(decSum(lines.flatMap((line) => (line.massMt === null ? [] : [line.massMt]))));
}

/**
 * Gönderimde (rapor kesinleşirken) bölümün sorunları; yollar `data.consumption.` ile başlar.
 * Dönem başlangıcı ve operasyon zorunlu; başlangıç raporun zamanından önce. Satırda ekipman,
 * yakıt, tank, yöntem, nitelik ve MT zorunlu; tahmini/hesaplanmış satır dayanak ister (sessiz
 * tahmin yok). Sayaç geri gidemez (reset kaydı hariç). Ayrımlar aralığın içinde, artan, dayanaklı,
 * satırlardaki yakıtlardan ve yakıt toplamını aşmaz.
 */
export function consumptionSubmitIssues(
  section: ConsumptionSection,
  reportedAt: string,
): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  const at = (path: string, code: string) =>
    issues.push({ path: `data.consumption.${path}`, code });
  const end = Date.parse(reportedAt);
  const start = section.periodStart ? Date.parse(section.periodStart) : null;
  if (section.periodStart === null) at('periodStart', 'REQUIRED');
  else if (start !== null && start >= end) at('periodStart', 'PERIOD_ORDER');
  if (section.operation === null) at('operation', 'REQUIRED');
  const keys = new Set<string>();
  section.lines.forEach((line, index) => {
    const path = (field: string) => `lines.${index}.${field}`;
    if (keys.has(line.key)) at(path('key'), 'DUPLICATE');
    keys.add(line.key);
    for (const field of LINE_REQUIRED) {
      if (line[field] === null) at(path(field), 'REQUIRED');
    }
    if ((line.quality === 'estimated' || line.quality === 'calculated') && line.basis === null) {
      at(path('basis'), 'REQUIRED');
    }
    const meter = line.meter;
    if (meter) {
      if ((meter.start === null) !== (meter.end === null)) {
        at(path(meter.start === null ? 'meter.start' : 'meter.end'), 'REQUIRED');
      }
      const delta = meterDelta(meter);
      if (delta && !delta.ok) at(path('meter.end'), 'METER_NEGATIVE');
      if (meter.reset) {
        if (meter.reset.rolloverAt === null) at(path('meter.reset.rolloverAt'), 'REQUIRED');
        if (meter.reset.at === null) at(path('meter.reset.at'), 'REQUIRED');
        else {
          const resetAt = Date.parse(meter.reset.at);
          if ((start !== null && resetAt < start) || resetAt >= end) {
            at(path('meter.reset.at'), 'RESET_OUTSIDE_PERIOD');
          }
        }
      }
    }
  });
  const totals = fuelTotals(section.lines);
  const splitSums: Record<string, string> = {};
  let previous = start;
  (section.splits ?? []).forEach((split, index) => {
    const path = (field: string) => `splits.${index}.${field}`;
    if (split.at === null) at(path('at'), 'REQUIRED');
    else {
      const splitAt = Date.parse(split.at);
      if ((start !== null && splitAt <= start) || splitAt >= end) {
        at(path('at'), 'SPLIT_OUTSIDE_PERIOD');
      } else if (previous !== null && splitAt <= previous && index > 0) {
        at(path('at'), 'SPLIT_ORDER');
      }
      previous = splitAt;
    }
    if (split.reason === null) at(path('reason'), 'REQUIRED');
    if (split.basis === null) at(path('basis'), 'REQUIRED');
    split.lines.forEach((part, partIndex) => {
      const partPath = (field: string) => path(`lines.${partIndex}.${field}`);
      if (part.fuelTypeId === null) at(partPath('fuelTypeId'), 'REQUIRED');
      if (part.massMt === null) at(partPath('massMt'), 'REQUIRED');
      if (part.fuelTypeId === null || part.massMt === null) return;
      if (totals[part.fuelTypeId] === undefined) {
        at(partPath('fuelTypeId'), 'SPLIT_FUEL_UNKNOWN');
        return;
      }
      splitSums[part.fuelTypeId] = formatDec(
        decAdd(splitSums[part.fuelTypeId] ?? '0', part.massMt),
      );
      if (decCmp(splitSums[part.fuelTypeId] as string, totals[part.fuelTypeId] as string) > 0) {
        at(partPath('massMt'), 'SPLIT_EXCEEDS_TOTAL');
      }
    });
  });
  return issues;
}

export type ConsumptionFlag =
  | { code: 'YEAR_SPLIT_MISSING' }
  | { code: 'METER_MASS_MISMATCH'; lineKey: string }
  | { code: 'ESTIMATED'; lineKey: string };

/**
 * Bölümün kalite bulguları (engel değil; MRV kalite listesine girer): yıl sınırını (31 Aralık
 * 24:00 UTC) geçen aralıkta o ana ayrım yoksa `REVIEW_REQUIRED` (T12; sessiz eşit dağıtım yok);
 * MT sayacının farkı tüketimle çelişiyor; tahmini satır.
 */
export function consumptionQuality(
  section: ConsumptionSection,
  reportedAt: string,
): { status: 'OK' | 'REVIEW_REQUIRED'; flags: ConsumptionFlag[] } {
  const flags: ConsumptionFlag[] = [];
  if (section.periodStart) {
    const splitTimes = new Set(
      (section.splits ?? [])
        .filter((s) => s.reason === 'year' && s.at !== null)
        .map((s) => new Date(s.at as string).toISOString()),
    );
    if (yearBoundariesIn(section.periodStart, reportedAt).some((b) => !splitTimes.has(b))) {
      flags.push({ code: 'YEAR_SPLIT_MISSING' });
    }
  }
  for (const line of section.lines) {
    if (line.meter?.unit === 'mt' && line.massMt !== null) {
      const delta = meterDelta(line.meter);
      if (delta?.ok && decCmp(delta.delta, line.massMt) !== 0) {
        flags.push({ code: 'METER_MASS_MISMATCH', lineKey: line.key });
      }
    }
    if (line.quality === 'estimated') flags.push({ code: 'ESTIMATED', lineKey: line.key });
  }
  return {
    status: flags.some((f) => f.code === 'YEAR_SPLIT_MISSING') ? 'REVIEW_REQUIRED' : 'OK',
    flags,
  };
}

// ---------- Defter ----------

/**
 * Raporun defter satırları (§2.2): satır başına kaynak tankta eksi `consumption`; anahtar satırın
 * anahtarıdır (idempotency: kaynak, revizyon, anahtar, tank). Sıfır satır hareket yazmaz. Ayrımlar
 * defteri bölmez: tek işlem (T12).
 */
export function reportConsumptionPosting(section: ConsumptionSection): LedgerLine[] {
  return section.lines.flatMap((line) =>
    line.tankId && line.fuelTypeId && line.massMt !== null && !decIsZero(line.massMt)
      ? [
          {
            kind: 'consumption' as const,
            tankId: line.tankId,
            fuelTypeId: line.fuelTypeId,
            batchId: line.batchId,
            massMt: formatDec(decNeg(line.massMt)),
            lineKey: line.key,
            density15: line.density15,
          },
        ]
      : [],
  );
}

// ---------- Eski alanların anlık görüntüsü ----------

const LEGACY_CODES = new Set<string>(FUEL_TYPES);
const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
/** Eski alanlar JS sayısıdır (MT, üç ondalık); negatif yazılamaz. */
const legacy = (value: string | undefined): number | null => {
  const rounded = formatDec(decRound(value ?? '0', 3));
  return rounded.startsWith('-') ? null : Number(rounded);
};

/**
 * Kesinleşen raporun eski alanları (§2.4): defteri açık gemide `rob[]` ve Noon `fuel.fuels[]`
 * defterden türetilir; `fuel.ts`, CP analizi, Voyage Summary ve altın test değişmeden çalışır.
 * Yalnız eski rapor kodu olan (`FUEL_TYPES`) yakıtlar yazılır; defterin bilmediği satırlar olduğu
 * gibi kalır. Negatif bakiye eski alana yazılmaz (uyarı Genel Bakış'ta).
 *
 * - ROB tablosu: `rob` = rapor anındaki defter ROB'u, `previousMt` = dönem başındaki, `consumptionMt`
 *   = satır toplamı, `bunkeredMt` = aralıkta onaylanmış alımlar (varsa; ADR 0090 Ek); günlük
 *   tüketim ve not korunur.
 * - Noon: `opening` = dönem başındaki ROB, `consumption` = satır toplamı, `bunkered` = aralıkta
 *   onaylanmış alımlar.
 */
export function consumptionSnapshot(input: {
  kind: VoyageReportKind;
  data: Readonly<Record<string, unknown>>;
  /** `fuelTypeId` → yakıt kodu. */
  codeOf: Readonly<Record<string, string>>;
  consumed: Readonly<Record<string, string>>;
  robAtStart: Readonly<Record<string, string>>;
  robAtEnd: Readonly<Record<string, string>>;
  bunkered: Readonly<Record<string, string>>;
}): Record<string, unknown> {
  const fuelIds = [
    ...new Set([
      ...Object.keys(input.robAtEnd),
      ...Object.keys(input.consumed),
      ...Object.keys(input.robAtStart),
    ]),
  ].filter((id) => LEGACY_CODES.has(input.codeOf[id] ?? ''));
  const derived = new Map<string, Record<string, unknown>>();
  for (const id of fuelIds) {
    const code = input.codeOf[id] as string;
    if (input.kind === 'noon') {
      const opening = legacy(input.robAtStart[id]);
      const consumption = legacy(input.consumed[id]);
      const bunkered = legacy(input.bunkered[id]);
      if (opening === null || consumption === null || bunkered === null) continue;
      derived.set(code, { fuel: code, opening, consumption, bunkered });
    } else {
      const rob = legacy(input.robAtEnd[id]);
      const previousMt = legacy(input.robAtStart[id]);
      const consumptionMt = legacy(input.consumed[id]);
      const bunkeredMt = legacy(input.bunkered[id]);
      if (rob === null) continue;
      // Aralıktaki alım ikmal sütunudur (ADR 0090 Ek): önceki − tüketim + ikmal = ROB. Alımsız
      // yakıtta alan yazılmaz (eski raporların içeriği değişmez)
      derived.set(code, {
        fuel: code,
        rob,
        previousMt,
        consumptionMt,
        ...(bunkeredMt ? { bunkeredMt } : {}),
      });
    }
  }
  const merge = (rows: unknown, keep: readonly string[]) => {
    const current = Array.isArray(rows) ? rows.map(record) : [];
    const used = new Set<string>();
    const out = current.flatMap((row) => {
      const code = typeof row.fuel === 'string' ? row.fuel : '';
      if (!fuelIds.some((id) => input.codeOf[id] === code)) return [row];
      used.add(code);
      const next = derived.get(code);
      if (!next) return [];
      const kept = Object.fromEntries(
        keep
          .filter((key) => row[key] !== undefined && row[key] !== null)
          .map((key) => [key, row[key]]),
      );
      return [{ ...next, ...kept }];
    });
    for (const [code, row] of derived) if (!used.has(code)) out.push(row);
    return out;
  };
  if (input.kind === 'noon') {
    const fuel = record(input.data.fuel);
    return { ...input.data, fuel: { ...fuel, fuels: merge(fuel.fuels, []) } };
  }
  return { ...input.data, rob: merge(input.data.rob, ['todayMt', 'remarks']) };
}
